// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { css } from '@emotion/react';

import {
  DateTime,
  formatToDateTime,
  formatToTimeShort,
  getDateTime,
} from 'infras/datetime';
import {
  spacing,
  fontSize,
  fontWeight,
  zIndex,
} from 'presentations/views/styles';
import { gray03, gray04 } from 'presentations/views/styles/color';
import Dialog from 'presentations/views/components/atoms/Dialog';
import Avatar from 'presentations/views/components/atoms/Avatar';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import DialogSubmitField from 'presentations/views/components/molecules/DialogSubmitField';
import { SharedGuestUserItem } from 'presentations/api/schema/user';

interface Props {
  onNext?: Function;
  onClose?: Function;
  selectedDatetime: DateTime | null;
  duration: number;
  participants: SharedGuestUserItem[];
  color?: string;
  isLightColor?: boolean;
  submitLabel?: string;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  return (
    <Dialog
      isOpen
      titleComponent={
        <TitleContainer>
          {t('title')}
          <CloseIconButton aria-label="close" onClick={props.onClose}>
            <CloseIcon name="close" size={fontSize(20)} />
          </CloseIconButton>
        </TitleContainer>
      }
      actions={
        <ActionContainer>
          <DialogSubmitField
            onSubmit={props.onNext}
            onCancel={props.onClose}
            submitLabel={props.submitLabel ?? t('submit')}
            color={props.color}
            isLightColor={props.isLightColor}
          />
        </ActionContainer>
      }
      onClose={props.onClose}
      scroll
      maxHeight="50vh"
      mobileMaxHeight="100%"
      zIndex={zIndex(1500)}
    >
      <Content>
        <DatetimeText>
          {formatToDateTime(props.selectedDatetime)} -{' '}
          {formatToTimeShort(
            getDateTime(props.selectedDatetime).plus({
              minute: props.duration,
            }),
          )}
        </DatetimeText>
        <ParticipantsText>{t('participants')}</ParticipantsText>
        {props.participants.map((participant, i) => (
          <ParticipantContainer key={participant.uid || i}>
            <StyledAvatar
              src={participant.profileImage}
              color={participant.color}
            >
              {participant.name.charAt(0)}
            </StyledAvatar>
            {participant.name}
          </ParticipantContainer>
        ))}
      </Content>
    </Dialog>
  );
}

const TitleContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: ${spacing(16)};
  border-bottom: 1px solid ${gray04};
  line-height: 44px;
  vertical-align: middle;
`;
const CloseIconButton = styled(IconButton)`
  position: absolute;
  right: 0;
  color: ${gray03};
`;
const CloseIcon = styled(Icon)``;

const ActionContainer = styled.div`
  padding-top: ${spacing(24)};
  border-top: 1px solid ${gray04};
`;

const Content = styled.div``;
const DatetimeText = styled.div`
  margin-bottom: ${spacing(12)};
  font-size: ${fontSize(18)};
  font-weight: ${fontWeight('bold')};
`;
const ParticipantsText = styled.div`
  margin-bottom: ${spacing(12)};
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
`;
const ParticipantContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(12)};

  &:last-of-type {
    margin-bottom: 0;
  }
`;
const StyledAvatar = styled(Avatar)<{ color: string }>`
  width: 20px;
  height: 20px;
  margin-right: ${spacing(4)};
  font-size: ${fontSize(12)};
  font-weight: initial;

  ${({ color }) => css`
    background-color: ${color};
  `}
`;
